
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, ArrowLeft } from 'lucide-react';
import { useAuthor } from '@/hooks/useAuthors';
import { useRoyaltyReports } from '@/hooks/useRoyaltyReports';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency, formatDate } from '@/utils/formatters';
import StatusBadge from '@/components/common/StatusBadge';
import CreateReportDialog from '@/components/admin/CreateReportDialog';

const AuthorRoyalties: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: author, isLoading: authorLoading } = useAuthor(id || '');
  const { data: royaltyReports, isLoading: reportsLoading } = useRoyaltyReports(id || '');
  const [searchQuery, setSearchQuery] = useState('');

  const isLoading = authorLoading || reportsLoading;

  // Filter reports based on search query
  const filteredReports = React.useMemo(() => {
    if (!royaltyReports) return [];
    
    if (!searchQuery) return royaltyReports;
    
    const query = searchQuery.toLowerCase();
    
    return royaltyReports.filter(report => 
      report.books?.title?.toLowerCase().includes(query) || 
      report.books?.isbn?.toLowerCase().includes(query) ||
      `Q${report.quarter} ${report.year}`.toLowerCase().includes(query)
    );
  }, [royaltyReports, searchQuery]);

  if (isLoading) {
    return (
      <MainLayout allowedRoles={['admin']}>
        <div className="flex justify-center p-8">
          <p>Loading...</p>
        </div>
      </MainLayout>
    );
  }

  if (!author) {
    return (
      <MainLayout allowedRoles={['admin']}>
        <div className="flex justify-center p-8">
          <p>Author not found</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout allowedRoles={['admin']}>
      <div className="mb-4">
        <Button
          variant="ghost"
          onClick={() => navigate(`/admin/authors/${id}`)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Author Profile
        </Button>
      </div>
      
      <PageHeader
        title={`${author.name}'s Royalties`}
        description={`Manage royalty reports for this author`}
      />
      
      <div className="mb-6">
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <CardTitle>Royalty Reports</CardTitle>
              
              <div className="flex w-full md:w-auto items-center gap-2">
                <div className="relative w-full md:w-80">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search reports..."
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                
                <CreateReportDialog authorId={id} />
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            {filteredReports.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Book</TableHead>
                    <TableHead>Sales</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReports.map(report => (
                    <TableRow key={report.id}>
                      <TableCell className="font-medium">Q{report.quarter} {report.year}</TableCell>
                      <TableCell>{report.books?.title || 'Unknown'}</TableCell>
                      <TableCell>{report.salesCount}</TableCell>
                      <TableCell>{formatCurrency(report.amount)}</TableCell>
                      <TableCell className="text-center">
                        <StatusBadge status={report.status} />
                      </TableCell>
                      <TableCell>{formatDate(report.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  {searchQuery ? 'No royalty reports found matching your search' : 'No royalty reports found for this author'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AuthorRoyalties;
